import { useRef, useState } from "react"
import { router } from "@inertiajs/react"
import { directUpload } from "@/lib/directUpload"
import { Download, Folder, Plus, Trash2 } from "lucide-react"

import { PanelCard, PanelCardHead, PanelCardBody } from "./PanelCard"
import PanelEmpty from "./PanelEmpty"
import { Button } from "@/components/ui/button"
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { fileSize } from "@/lib/fileSize"
import { csrfToken } from "@/lib/orderActions"
import { cn } from "@/lib/utils"

const KIND_BG = {
  doc: "bg-chart-1",
  pdf: "bg-[#EF4444]",
  zip: "bg-chart-4",
  img: "bg-chart-2",
}

function extOf(file) {
  const match = (file.fileName || "").match(/\.([a-z0-9]+)$/i)
  const ext = match ? match[1] : (file.fileType || "file").split("/").pop()
  return (ext || "FILE").toUpperCase().slice(0, 4)
}

function kindOf(file) {
  const type = file.fileType || ""
  if (type.includes("pdf")) return "pdf"
  if (type.includes("zip") || type.includes("compressed")) return "zip"
  if (type.startsWith("image/")) return "img"
  return "doc"
}

function FileCard({ file, onRemove }) {
  const meta = [fileSize(file.fileSize), file.uploadedAt].filter(Boolean).join(" · ")
  const uploader = file.uploadedBy

  // The card is a group of controls now (download, and remove where allowed),
  // so it is no longer one big download link: the name and the download icon
  // download, the trash icon asks before removing (AU-39).
  return (
    <div
      data-testid="shared-file-card"
      className="flex flex-col gap-3 rounded-2xl border border-border bg-card p-4 transition-colors hover:border-line-2"
    >
      <div className="flex items-center gap-[11px]">
        <span className={cn("grid size-[42px] shrink-0 place-items-center rounded-[10px] font-display text-[10px] font-bold text-white", KIND_BG[kindOf(file)])}>
          {extOf(file)}
        </span>
        <div className="min-w-0">
          <a href={file.url} download={file.fileName} className="text-[13px] leading-[1.25] font-semibold break-words hover:underline">
            {file.fileName}
          </a>
          {meta && <div className="mt-0.5 text-[11px] text-ink-3">{meta}</div>}
        </div>
      </div>
      <div className="mt-auto flex items-center justify-between gap-2">
        {/* Unattributed files keep the row's shape so the controls stay put. */}
        {uploader ? (
          <span className="flex min-w-0 items-center gap-1.5">
            <Avatar className="size-[22px]">
              {uploader.avatarUrl && <AvatarImage src={uploader.avatarUrl} alt="" />}
              <AvatarFallback className="text-[9px]">{uploader.initials}</AvatarFallback>
            </Avatar>
            <span className="truncate text-[11px] text-ink-3">{uploader.name}</span>
          </span>
        ) : (
          <span />
        )}
        <span className="flex shrink-0 items-center gap-1.5">
          {file.deletable && (
            <Button
              type="button"
              variant="outline"
              size="icon"
              className="size-[30px] rounded-lg text-muted-foreground hover:text-destructive"
              aria-label={`Remove ${file.fileName}`}
              title="Remove"
              onClick={() => onRemove(file)}
            >
              <Trash2 className="size-[15px]" strokeWidth={2} />
            </Button>
          )}
          <Button type="button" variant="outline" size="icon" className="size-[30px] rounded-lg text-muted-foreground" asChild>
            <a href={file.url} download={file.fileName} aria-label={`Download ${file.fileName}`} title="Download">
              <Download className="size-[15px]" strokeWidth={2} />
            </a>
          </Button>
        </span>
      </div>
    </div>
  )
}

// Shared Files tab — the order's real customer-shared uploads from S3
// (extra_file_uploads). Mirrors order-shared-files.html.
export default function SharedFilesPanel({ order }) {
  const files = order.deliverables?.sharedFiles ?? []
  const canAdd = Boolean(order.permissions?.addFile)
  const fileInputRef = useRef(null)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState("")
  // The file whose removal is awaiting confirmation; null when the dialog is closed.
  const [removing, setRemoving] = useState(null)
  const [removingBusy, setRemovingBusy] = useState(false)

  // Each file goes straight to storage first; the FormData through fetch then
  // carries only signed ids. The endpoint answers JSON, so the page is
  // reloaded on success to pick the new file up through the normal payload.
  async function uploadFiles(event) {
    const chosen = Array.from(event.target.files || [])
    event.target.value = ""
    if (!chosen.length) return

    setSaving(true)
    setError("")

    try {
      const signedIds = await Promise.all(chosen.map(directUpload))
      const body = new FormData()
      signedIds.forEach((signedId) => body.append("files[]", signedId))

      const response = await fetch(`/projects/${order.id}/files`, {
        method: "POST",
        headers: { "X-CSRF-Token": csrfToken(), Accept: "application/json" },
        body,
      })
      const payload = await response.json().catch(() => ({}))
      if (!response.ok) throw new Error(payload.error || "")
      router.reload({ only: ["order"] })
    } catch (uploadError) {
      setError(uploadError.message || "We couldn't upload that file. Please try again.")
    } finally {
      setSaving(false)
    }
  }

  // DELETE through fetch like the upload, then reload the order so the panel
  // reflects the server's list rather than a locally patched one.
  async function removeFile() {
    const file = removing
    if (!file) return
    setRemovingBusy(true)
    setError("")

    try {
      const response = await fetch(`/projects/${order.id}/files/${file.attachmentId}`, {
        method: "DELETE",
        headers: { "X-CSRF-Token": csrfToken(), Accept: "application/json" },
        credentials: "same-origin",
      })
      const payload = await response.json().catch(() => ({}))
      // Already gone (someone else removed it first): say so, and reload so the
      // stale card leaves the panel too.
      if (response.status === 404) router.reload({ only: ["order"] })
      if (!response.ok) throw new Error(payload.error || "")
      setRemoving(null)
      router.reload({ only: ["order"] })
    } catch (removeError) {
      setRemoving(null)
      setError(removeError.message || "We couldn't remove that file. Please try again.")
    } finally {
      setRemovingBusy(false)
    }
  }

  const confirmRemove = (
    <Dialog open={Boolean(removing)} onOpenChange={(open) => !open && !removingBusy && setRemoving(null)}>
      <DialogContent className="max-w-[420px]">
        <DialogHeader>
          <DialogTitle>Remove this file?</DialogTitle>
          <DialogDescription>
            {removing ? `${removing.fileName} will be removed from this project for everyone. This can't be undone.` : ""}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button type="button" variant="outline" disabled={removingBusy} onClick={() => setRemoving(null)}>
            Keep it
          </Button>
          <Button type="button" variant="destructive" disabled={removingBusy} onClick={removeFile}>
            {removingBusy ? "Removing…" : "Remove file"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )

  const picker = canAdd && (
    <input
      ref={fileInputRef}
      type="file"
      multiple
      onChange={uploadFiles}
      className="sr-only"
      tabIndex={-1}
      aria-label="Choose files to share"
    />
  )

  const addButton = canAdd && (
    <Button type="button" variant="outline" size="sm" disabled={saving} onClick={() => fileInputRef.current?.click()}>
      <Plus className="size-3.5" />
      {saving ? "Uploading…" : "Add file"}
    </Button>
  )

  if (!files.length) {
    return (
      <>
        {picker}
        <PanelEmpty
          Icon={Folder}
          title="Shared Files"
          heading="No shared files yet"
          body="Briefs, references and deliverables your team shares will collect here."
          action={
            canAdd && (
              <>
                <Button type="button" size="sm" disabled={saving} onClick={() => fileInputRef.current?.click()}>
                  <Plus className="size-3.5" />
                  {saving ? "Uploading…" : "Add a file"}
                </Button>
                {error && <p role="alert" className="mt-2 text-xs font-medium text-primary">{error}</p>}
              </>
            )
          }
        />
      </>
    )
  }

  return (
    <PanelCard>
      {picker}
      <PanelCardHead Icon={Folder} title="Shared Files">
        <div className="flex items-center gap-2">
          {addButton}
          <Button type="button" variant="outline" size="sm" asChild>
            <a href={`/files/order/download/shared/files?order=${order.id}`}>
              <Download className="size-3.5" />
              Download all
            </a>
          </Button>
        </div>
      </PanelCardHead>
      <PanelCardBody className="@container">
        {error && <p role="alert" className="mb-3 text-xs font-medium text-primary">{error}</p>}
        {/* Columns follow the PANEL's width, not the window's (AU-38 / UPDA-84).
            At 861px the window is "wide", but the sidebar and the tab rail
            beside this panel leave it ~280px, and three viewport-keyed columns
            gave each card ~90px — the title wrapped one letter per line. A
            card reads from ~170px, so: two columns from 480px of panel, three
            from 540px. The Switchback recipe (P8-H3) still holds at 1112
            (three across, the panel being ~556) and at 365 (stacked). The
            departure: a full-width panel shows three columns from a ~602px
            window (the recipe said two up to 860) and two from ~534 (the
            recipe said one up to 560); cards never drop below ~170px. */}
        <div className="grid grid-cols-1 gap-3.5 @min-[480px]:grid-cols-2 @min-[540px]:grid-cols-3">
          {files.map((file, i) => (
            <FileCard key={file.attachmentId ?? `${file.fileName}-${i}`} file={file} onRemove={setRemoving} />
          ))}
        </div>
      </PanelCardBody>
      {confirmRemove}
    </PanelCard>
  )
}
